import { type FC } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useExportChannel } from '@/hooks/websocket/useExportChannel';
import { useExportStore } from '@/hooks/data/export/useExportStore';
import { useCapabilities } from '@/hooks/useCapabilities';

const ExportChannelListener = ({ exportId, shareId }: { exportId: string; shareId?: string }) => {
  useExportChannel(exportId, { shareId });
  return null;
};

/**
 * Mounts one WebSocket listener per active export — share-scoped and
 * authenticated DAM exports alike. Both broadcast on the same private
 * `export.{id}` channel (the backend no longer distinguishes them), and every
 * remaining share-export creator (OTP guest or tenant member) is
 * Auth0-authenticated, so there's no longer a separate public-channel path.
 * Keeps the store a pure data model: it reads the active exports and
 * subscribes a channel for each, the same pattern as
 * AssetVersionCreatedListener. Renders nothing.
 */
export const ExportProgressManager = () => {
  const exports = useExportStore(useShallow((state) => state.exports));

  return (
    <>
      {exports.map((item) => (
        <ExportChannelListener key={item.id} exportId={item.id} shareId={item.shareId} />
      ))}
    </>
  );
};

/**
 * Mounted above the page tree (in _app.tsx) so it survives page navigation. Gated on
 * canDownloadAssets so an anonymous share viewer never opens a channel for a stale export.
 * canDownloadAssets never reads useShareConfig, which is null this high up.
 */
export const GatedExportProgressManager: FC = () => {
  const { canDownloadAssets } = useCapabilities();
  return canDownloadAssets ? <ExportProgressManager /> : null;
};
